import {
    IMappingProfile,
    IQueryParams,
    IQueryParamsSymbol,
    PFWmsEntities,
} from '@pf/shared-common';
import { createMap, Mapper } from '@automapper/core';
import {
    entitySymbols,
    withArraySearchFilter,
    withDefaultEntityMetadata,
    withDefaultSearchParamsMappingConfiguration,
    withDefaultSearchParamsMetadata,
    withFragmentIdProp,
    withFragmentSearchFilter,
    withMetadata,
    withSearchFilter,
} from '@pf/shared-utility';
import {
    ProjectCreate,
    ProjectDto,
    ProjectRead,
    ProjectSearchParams,
} from '../../entities/project.dto';
import {
    CustomFieldMetadataMap,
    DefaultLocationFragmentMetadataMap,
    ITypeEntityFragmentSymbol,
    ResourceAuthSymbol,
    UserFragmentSymbol,
} from '@pf/shared/util-platform';
import { ProjectLineItemMetadataMap } from '../project-line-items/project-line-item.mapping-profile';
import { withWarehouseSearchFilter } from '../wms-mapping.util';

const symbols = entitySymbols(PFWmsEntities.Project);

withDefaultEntityMetadata<ProjectDto>(symbols.dto, {
    customFields: CustomFieldMetadataMap.symbols.dto,
    endDate: String,
    externalReferenceId: String,
    lineItems: ProjectLineItemMetadataMap.symbols.dto,
    notes: String,
    number: String,
    primaryLocation: DefaultLocationFragmentMetadataMap.symbols.dto,
    priority: String,
    projectType: ITypeEntityFragmentSymbol,
    requestingContact: [Object],
    resourceAuth: ResourceAuthSymbol,
    secondaryLocation: DefaultLocationFragmentMetadataMap.symbols.dto,
    startDate: String,
    status: String,
    warehouse: [Object],
    assignedToUser: UserFragmentSymbol,
    packingLists: [Object],
});

withDefaultEntityMetadata<ProjectRead>(symbols.entity, {
    customFields: CustomFieldMetadataMap.symbols.entity,
    endDate: String,
    externalReferenceId: String,
    lineItems: ProjectLineItemMetadataMap.symbols.entity,
    notes: String,
    number: String,
    primaryLocation: DefaultLocationFragmentMetadataMap.symbols.entity,
    priority: String,
    projectType: ITypeEntityFragmentSymbol,
    requestingContact: [Object],
    resourceAuth: ResourceAuthSymbol,
    secondaryLocation: DefaultLocationFragmentMetadataMap.symbols.entity,
    startDate: String,
    status: String,
    warehouse: [Object],
    assignedToUser: UserFragmentSymbol,
    packingLists: [Object],
});

withMetadata<ProjectCreate>(symbols.createBody, {
    endDate: String,
    externalReferenceId: String,
    notes: String,
    number: String,
    primaryLocationId: String,
    priority: String,
    projectTypeId: String,
    requestingContactId: String,
    resourceAuth: ResourceAuthSymbol,
    secondaryLocationId: String,
    startDate: String,
    status: String,
    warehouseId: String,
    assignedToUserId: String,
});

withDefaultSearchParamsMetadata<ProjectSearchParams>(symbols.searchParams, {
    ids: [String],
    startDateRangeStart: String,
    startDateRangeEnd: String,
    endDateRangeStart: String,
    endDateRangeEnd: String,
    status: [String],
    priority: String,
    externalReferenceId: String,
    number: String,
    projectTypeId: String,
    warehouseId: String,
    assignedToUserId: String,
});

export const ProjectMetadataMap: IMappingProfile = {
    symbols,
    profile: (mapper: Mapper) => {
        createMap<ProjectRead, ProjectDto>(mapper, symbols.entity, symbols.dto);
        createMap<ProjectDto, ProjectRead>(mapper, symbols.dto, symbols.entity);
        createMap<ProjectDto, ProjectCreate>(
            mapper,
            symbols.dto,
            symbols.createBody,
            withFragmentIdProp('assignedToUser'),
            withFragmentIdProp('requestingContact')
        );
        createMap<IQueryParams<ProjectDto>, ProjectSearchParams>(
            mapper,
            IQueryParamsSymbol,
            symbols.searchParams,
            withArraySearchFilter('status'),
            withSearchFilter('priority'),
            withSearchFilter('externalReferenceId'),
            withFragmentSearchFilter('assignedToUser'),
            withSearchFilter('number', 'search'),
            withSearchFilter('projectType', 'projectTypeId'),
            withWarehouseSearchFilter(),
            ...withDefaultSearchParamsMappingConfiguration()
        );

        CustomFieldMetadataMap.profile(mapper);
        ProjectLineItemMetadataMap.profile(mapper);
        DefaultLocationFragmentMetadataMap.profile(mapper);
    },
};
